import FluxUtils from 'flux/utils';
import Immutable from 'immutable';

import type { ActionType } from 'sharedreact/actions/generic/actions';
import AuthenticationConstants from 'sharedreact/constants/AuthenticationConstants';
import SessionWarningMode from 'sharedreact/constants/SessionWarningMode';
import AuthenticationMethod from 'sharedreact/data/AuthenticationMethod';
import User from 'sharedreact/data/User';
import AppDispatcher from 'sharedreact/dispatcher/AppDispatcher';
import ConnectionSupport from 'sharedreact/utils/ConnectionSupport';

const DEFAULT_SESSION_TIMEOUT: number = 600;
const DEFAULT_MAX_EXTEND_SESSION_TIMES: number = 11;

class KeepAlive {
  _interval: number | undefined;

  _expirationTime: number;
  _maxKeepAliveTime: number;

  _servicesPath: string | null;

  constructor (servicesPath: string | null, sessionTimeout?: number, maxExtendTimes?: number) {
    this.execute = this.execute.bind(this);
    this.cancel = this.cancel.bind(this);

    const _sessionTimeout: number = (sessionTimeout || DEFAULT_SESSION_TIMEOUT);
    if (maxExtendTimes === -1) {
      this._expirationTime = -1;
      this._maxKeepAliveTime = -1;
    } else {
      const _maxExtendTimes: number = maxExtendTimes || DEFAULT_MAX_EXTEND_SESSION_TIMES;

      this._expirationTime = _sessionTimeout * (_maxExtendTimes + 1);
      this._maxKeepAliveTime = this._expirationTime - _sessionTimeout;
    }

    const intervalTimeout = Math.min(_sessionTimeout * 500, 15 * 60 * 1000);

    this._interval = window.setInterval(this.execute, intervalTimeout);
    this._servicesPath = servicesPath;
  }

  execute (): void {
    const currentTime: number = Math.floor(new Date().getTime() / 1000);

    const lastRequestSentTime: number = Math.floor(ConnectionSupport.getLastRequestSentTime() / 1000);
    if (this._maxKeepAliveTime === -1 || currentTime < lastRequestSentTime + this._maxKeepAliveTime) {
      ConnectionSupport.sendHeartbeat(this._servicesPath ? this._servicesPath + '/heartbeat' : null);
    } else if (currentTime > lastRequestSentTime + this._expirationTime) {
      ConnectionSupport.isSessionStillValid(
        () => {},
        () => {
          this.cancel();
        },
        (this._servicesPath ? this._servicesPath + '/isSessionStillValid' : null)
      );
    }
  }

  cancel (): void {
    window.clearInterval(this._interval);
  }
}

const Content: {
  isLoggedIn: boolean,
  user: User | null,
  authenticationMethods: Immutable.List<AuthenticationMethod>,
  attemptRelogin: boolean,
  sessionId: string | null,
  isSlideOutVisible: boolean,
  changePasswordRequestFinished: boolean,
  changePasswordRequestResult: boolean,
  forgotPasswordMailHasBeenSent: boolean,
  sessionWarningMode: number,
  locations: Immutable.List<string>,
  serverVersion: string
} = {
  isLoggedIn: false,
  user: null,
  authenticationMethods: Immutable.List(),
  attemptRelogin: true,
  sessionId: null,
  isSlideOutVisible: false,
  changePasswordRequestFinished: false,
  changePasswordRequestResult: false,
  forgotPasswordMailHasBeenSent: false,
  sessionWarningMode: SessionWarningMode.NoWarning,
  locations: Immutable.List(),
  serverVersion: ''
};

class State extends Immutable.Record(Content) {
}

class AuthenticationStore extends FluxUtils.ReduceStore<State, ActionType> {
  _keepAlive: KeepAlive | null

  constructor () {
    super(AppDispatcher);
    
    this._keepAlive = null;
  }

  override getInitialState (): State {
    return new State();
  }

  isLoggedIn (): boolean {
    return this.getState().isLoggedIn;
  }

  getUser (): User {
    return this.getState().user!;
  }

  isAttemptRelogin (): boolean {
    return this.getState().attemptRelogin;
  }

  getAuthenticationMethods (): Immutable.List<AuthenticationMethod> {
    return this.getState().authenticationMethods;
  }

  getSessionId (): string {
    return this.getState().sessionId!;
  }

  getLocations (): Immutable.List<string> {
    return this.getState().locations;
  }

  getServerVersion (): string {
    return this.getState().serverVersion;
  }

  override reduce (state: State, action: ActionType): State {
    switch (action.actionType) {
      case AuthenticationConstants.SET_SESSION_WARNING_MODE: {
        if (action.mode === SessionWarningMode.Expired) {
          if (this._keepAlive != null) {
            this._keepAlive.cancel();
            this._keepAlive = null;
          }
        }
        
        if (this.getState().isLoggedIn || action.mode === SessionWarningMode.NoWarning) {
          return state.set('sessionWarningMode', action.mode);
        }

        return state;
      }
      case AuthenticationConstants.GET_SERVER_VERSION_SUCCESS: {
        return state.set('serverVersion', action.serverVersion);
      }
      case AuthenticationConstants.LOGIN: {
        return state;
      }
      case AuthenticationConstants.LOGIN_SUCCESS: {
        this._keepAlive = new KeepAlive(action.servicesPath, action.sessionTimeout, action.maxExtendTimes);

        return state.set('isLoggedIn', true)
          .set('user', action.item)
          .set('attemptRelogin', false)
          .set('sessionWarningMode', SessionWarningMode.NoWarning)
          .set('locations', action.locations || Immutable.List());
      }
      case AuthenticationConstants.RELOGIN: {
        return state.set('attemptRelogin', true);
      }
      case AuthenticationConstants.RELOGIN_FAIL: {
        return state
          .set('attemptRelogin', false)
          .set('isLoggedIn', false);
      }
      case AuthenticationConstants.LOGOUT: {
        if (this._keepAlive != null) {
          this._keepAlive.cancel();
          this._keepAlive = null;
        }

        return state;
      }
      case AuthenticationConstants.LOGOUT_SUCCESS: {
        return state.set('isLoggedIn', false)
          .set('sessionWarningMode', SessionWarningMode.NoWarning);
      }
      case AuthenticationConstants.GET_LOGON_METHODS_SUCCESS: {
        return state.set('authenticationMethods', action.authenticationMethods);
      }
      case AuthenticationConstants.GET_SESSION_ID_SUCCESS: {
        return state.set('sessionId', action.sessionId);
      }
      case AuthenticationConstants.TOGGLE_SLIDE_OUT: {
        return state.set('isSlideOutVisible', !state.isSlideOutVisible);
      }
      case AuthenticationConstants.HIDE_SLIDE_OUT: {
        return state.set('isSlideOutVisible', false);
      }
      case AuthenticationConstants.CHANGE_PASSWORD_RESET: {
        return state.set('changePasswordRequestFinished', false);
      }
      case AuthenticationConstants.CHANGE_PASSWORD_SUCCESS: {
        return state.set('changePasswordRequestFinished', true)
          .set('changePasswordRequestResult', true);
      }
      case AuthenticationConstants.CHANGE_PASSWORD_FAIL: {
        return state.set('changePasswordRequestFinished', true)
          .set('changePasswordRequestResult', false);
      }
      case AuthenticationConstants.FORGOT_PASSWORD_SUCCESS: {
        return state.set('forgotPasswordMailHasBeenSent', true);
      }
      case AuthenticationConstants.CLEAR_FORGOT_PASSWORD_EMAIL_SENT: {
        return state.set('forgotPasswordMailHasBeenSent', false);
      }
    }
    return state;
  }
}

export default new AuthenticationStore();
